import { Injectable, inject } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import { VenuesGateway } from '../../gateways/venues.gateway'
import { AppState } from '../helper.state'
import { Store } from '@ngrx/store'
import { VenueActions } from './venue.actions'
import {
	getLocation,
	getMichelinGuide,
	getNewRestaurant,
	getIncludeBenefits,
	getSelectedCategories,
	getSelectedVenue,
	getVenueName,
	getVenues,
	getExcludeBenefits,
	getStatusVenues,
	getVenueIds,
} from './venue.selectors'
import { catchError, combineLatest, concatMap, delay, filter, map, of, switchMap, take } from 'rxjs'
import { getSelectedAddress } from '../address/address.selectors'
import { getSelectedNeighborhood } from '../address/address.selectors'
import { getClient, getStatusClient } from '../client/client.selectors'
import { getSelectedCuisines } from '../cuisine/cuisine.selectors'
import { DEFAULT_CUISINE_TEXT } from '../cuisine/cuisine.state'
import { GeolocationService } from '@monorepo-channels/shared/util-helpers'
import { ClientActions } from '../client/client.actions'
import { AuthService } from '../../auth/auth.service'

@Injectable()
export class VenueEffects {
	private actions$ = inject(Actions)
	private venueGateway = inject(VenuesGateway)
	private store = inject(Store<AppState>)
	private geolocation = inject(GeolocationService)
	private authService = inject(AuthService)

	// Cached
	getVenues$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.getVenues),
			concatLatestFrom(() => this.store.select(getStatusVenues)),
			filter(([, status]) => status === 'idle'),
			map(() => VenueActions.loadVenues({ filterVenuesDto: {} }))
		)
	})

	refreshVenuesOnLogin$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ClientActions.loadClientSuccess),
			concatLatestFrom(() => this.store.select(getLocation)),
			map(([, location]) =>
				VenueActions.loadVenues({
					filterVenuesDto: {
						venuesAlreadyInView: [],
						...(location ? { address: { location } } : {}),
					},
				})
			)
		)
	})

	// Call API (not cached)
	loadVenues$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.loadVenues),
			switchMap(action =>
				this.store.select(getClient).pipe(
					take(1),
					switchMap(client => {
						const finalFilterDto = {
							...action.filterVenuesDto,
							categoryId: client?.selectedCategory?.categoryId,
						}
						return this.venueGateway.filter(finalFilterDto).pipe(
							map(venues => VenueActions.loadVenuesSuccess({ venues })),
							catchError(error => of(VenueActions.loadVenuesFail({ error })))
						)
					})
				)
			)
		)
	})

	loadVenuesFilterByName$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.filterVenuesByName),
			concatLatestFrom(() => this.store.select(getClient)),
			concatMap(([action, client]) =>
				this.venueGateway
					.filter({
						...action.filterVenuesDto,
						categoryId: client?.selectedCategory?.categoryId,
					})
					.pipe(
						map(venuesFilter => VenueActions.filterVenuesByNameSuccess({ venuesFilter })),
						catchError(error => of(VenueActions.filterVenuesByNameFail({ error })))
					)
			)
		)
	})

	// This actions dispatch another action to LoadVenues
	loadVenuesByFilters$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.applyFilter, VenueActions.filterVenuesByNameV2),
			switchMap(() =>
				// ← switchMap cancela processamentos anteriores
				combineLatest([
					this.store.select(getSelectedCuisines),
					this.store.select(getSelectedAddress),
					this.store.select(getLocation),
					this.store.select(getSelectedNeighborhood),
					this.store.select(getVenueName),
					this.store.select(getSelectedCategories),
					this.store.select(getMichelinGuide),
					this.store.select(getNewRestaurant),
					this.store.select(getIncludeBenefits),
					this.store.select(getExcludeBenefits),
					this.store.select(getVenueIds),
				]).pipe(
					take(1),
					map(
						([
							cuisines,
							address,
							location,
							neighborhood,
							name,
							categories,
							michelinGuide,
							newRestaurant,
							includeBenefits,
							excludeBenefits,
							venueIds,
						]) => {
							const cuisinesResult = cuisines
								.map(cuisine =>
									cuisine.name === DEFAULT_CUISINE_TEXT || !cuisine.name
										? undefined
										: cuisine._id
								)
								.filter(Boolean)
							const neighborhoodResult = neighborhood
								? { neighborhood, city: address?.city }
								: address?.city
								  ? { city: address?.city }
								  : undefined
							const locationResult = location ? { location } : undefined
							return VenueActions.loadVenues({
								filterVenuesDto: {
									name,
									cousine: cuisinesResult.length ? cuisinesResult : undefined,
									address: neighborhoodResult ?? locationResult,
									venuesAlreadyInView: [],
									type: categories.length ? categories : undefined,
									...(michelinGuide ? { michelinGuide } : {}),
									...(newRestaurant ? { createdAt: newRestaurant.toISOString() } : {}),
									...(includeBenefits.length > 0 ? { includeBenefits } : {}),
									...(excludeBenefits.length > 0 ? { excludeBenefits } : {}),
									...(venueIds.length > 0 ? { venueIds } : {}),
								},
							})
						}
					)
				)
			)
		)
	})

	cleanFilterVenuesByName$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.cleanFilterVenuesByName),
			switchMap(() =>
				// ← mesmo padrão
				combineLatest([
					this.store.select(getSelectedCuisines),
					this.store.select(getSelectedAddress),
					this.store.select(getLocation),
					this.store.select(getSelectedNeighborhood),
					this.store.select(getSelectedCategories),
					this.store.select(getMichelinGuide),
					this.store.select(getNewRestaurant),
					this.store.select(getIncludeBenefits),
					this.store.select(getExcludeBenefits),
					this.store.select(getVenueIds),
				]).pipe(
					take(1),
					map(
						([
							cuisines,
							address,
							location,
							neighborhood,
							categories,
							michelinGuide,
							newRestaurant,
							includeBenefits,
							excludeBenefits,
							venueIds,
						]) => {
							const cuisinesResult = cuisines
								.map(cuisine =>
									cuisine.name === DEFAULT_CUISINE_TEXT || !cuisine.name
										? undefined
										: cuisine._id
								)
								.filter(Boolean)
							const neighborhoodResult = neighborhood
								? { neighborhood, city: address?.city }
								: address?.city
								  ? { city: address?.city }
								  : undefined
							const locationResult = location ? { location } : undefined
							return VenueActions.loadVenues({
								filterVenuesDto: {
									name: undefined,
									cousine: cuisinesResult.length ? cuisinesResult : undefined,
									address: neighborhoodResult ?? locationResult,
									venuesAlreadyInView: [],
									type: categories.length ? categories : undefined,
									...(michelinGuide ? { michelinGuide } : {}),
									...(newRestaurant ? { createdAt: newRestaurant.toISOString() } : {}),
									...(includeBenefits.length > 0 ? { includeBenefits } : {}),
									...(excludeBenefits.length > 0 ? { excludeBenefits } : {}),
									...(venueIds.length > 0 ? { venueIds } : {}),
								},
							})
						}
					)
				)
			)
		)
	})

	// // Cached
	getSelectedVenue$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.getOneVenue),
			concatLatestFrom(() => this.store.select(getSelectedVenue)),
			// Only load single venue if null
			filter(([, selectedVenue]) => !selectedVenue),
			map(([action]) => VenueActions.loadOneVenue({ venueId: action.venueId }))
		)
	})

	loadOneVenue$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.loadOneVenue),
			switchMap(action => {
				// If there's no session token, the client will never load - don't wait for it.
				const { token, clientId } = this.authService.getClientIdAndToken()
				const hasSession = !!(token && clientId)

				return this.store.select(getStatusClient).pipe(
					// Wait until the client finished loading (success or failure) before fetching,
					// so the request can include the client's categoryId. This avoids a race on
					// page refresh where the venue would otherwise be fetched before the client is ready.
					filter(status => !hasSession || (status !== 'idle' && status !== 'pending')),
					take(1),
					// loadClientSuccess synchronously triggers a follow-up dispatch that finalizes
					// selectedCategory (auto-selecting the best card, or restoring a saved one - see
					// changeCategory$/restoreSelectedCategory$ in client.effects.ts). Defer to the next
					// tick so that follow-up has already landed before we read the client's category.
					delay(0),
					concatLatestFrom(() => this.store.select(getClient)),
					switchMap(([, client]) => {
						const categoryId = client?.selectedCategory?.categoryId

						return this.venueGateway
							.getVenueId(action.venueId, {
								...action.findOneVenueDto,
								...(categoryId ? { categoryId } : {}),
							})
							.pipe(
								map(venue => VenueActions.loadOneVenueSuccess({ venue })),
								catchError(error => of(VenueActions.loadOneVenueFail({ error })))
							)
					})
				)
			})
		)
	})

	filterVenuesByIds$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.filterVenuesByIds),
			concatLatestFrom(() => this.store.select(getClient)),
			switchMap(([{ venueIds }, client]) =>
				this.venueGateway
					.filter({
						venueIds,
						categoryId: client?.selectedCategory?.categoryId,
					})
					.pipe(
						map(venues => VenueActions.filterVenuesByIdsSuccess({ filteredVenues: venues })),
						catchError(error => of(VenueActions.filterVenuesByIdsFailure({ error })))
					)
			)
		)
	})

	loadMoreVenues$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.loadMoreVenue),
			concatLatestFrom(() => {
				return [
					this.store.select(getVenues),
					this.store.select(getSelectedCuisines),
					this.store.select(getSelectedAddress),
					this.store.select(getClient),
					this.store.select(getLocation),
					this.store.select(getSelectedNeighborhood),
					this.store.select(getVenueName),
					this.store.select(getSelectedCategories),
					this.store.select(getMichelinGuide),
					this.store.select(getNewRestaurant),
					this.store.select(getIncludeBenefits),
					this.store.select(getExcludeBenefits),
					this.store.select(getVenueIds),
				]
			}),
			concatMap(
				([
					,
					venues,
					cuisines,
					address,
					client,
					location,
					neighborhood,
					name,
					categories,
					michelinGuide,
					newRestaurant,
					includeBenefits,
					excludeBenefits,
					venueIds,
				]) => {
					// TODO: duplicate logic in loadVenuesByFilter$
					const cuisinesResult = cuisines
						.map(cuisine =>
							cuisine.name === DEFAULT_CUISINE_TEXT || !cuisine.name ? undefined : cuisine._id
						)
						.filter(Boolean)
					const neighborhoodResult = neighborhood
						? { neighborhood, city: address?.city }
						: address?.city
						  ? { city: address?.city }
						  : undefined
					const locationResult = location ? { location } : undefined
					return this.venueGateway.filter({
						name: name,
						cousine: cuisinesResult.length ? cuisinesResult : undefined,
						address: neighborhoodResult ?? locationResult,
						venuesAlreadyInView: venues?.map(venue => venue.id),
						categoryId: client?.selectedCategory?.categoryId,
						type: categories.length ? categories : undefined,
						...(michelinGuide ? { michelinGuide } : {}),
						...(newRestaurant ? { createdAt: newRestaurant.toISOString() } : {}),
						...(includeBenefits.length > 0 ? { includeBenefits } : {}),
						...(excludeBenefits.length > 0 ? { excludeBenefits } : {}),
						...(venueIds.length > 0 ? { venueIds } : {}),
					})
				}
			),
			map(venues => VenueActions.loadMoreVenueSuccess({ venues })),
			catchError(error => of(VenueActions.loadMoreVenueFail({ error })))
		)
	})

	loadVenuesByLocation$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.setLocation),
			filter(() => this.geolocation.firstTime),
			take(1),
			concatLatestFrom(() => [this.store.select(getVenues)]),
			filter(() => this.geolocation.firstTime),
			map(([{ latitude, longitude, maxDistance }]) => {
				this.geolocation.firstTime = false
				return VenueActions.loadVenues({
					filterVenuesDto: {
						address: { location: { latitude, longitude, maxDistance } },
						venuesAlreadyInView: [],
					},
				})
			})
		)
	})

	loadVenuesBySelectedCity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.selectCityAndLoadVenues),
			concatLatestFrom(() => this.store.select(getClient)),
			concatMap(([{ latitude, longitude, maxDistance }, client]) => {
				const filterDtoForLoadVenues = {
					address: { location: { latitude, longitude, maxDistance } },
					venuesAlreadyInView: [],
					categoryId: client?.selectedCategory?.categoryId,
				}

				return this.venueGateway.filter(filterDtoForLoadVenues).pipe(
					map(venues => VenueActions.loadVenuesFromSelectedCitySuccess({ venues })),
					catchError(error => of(VenueActions.loadVenuesFromSelectedCityFail({ error })))
				)
			})
		)
	})

	filterVenuesByFavorites$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(VenueActions.filterVenuesByFavorites),
			concatLatestFrom(() => this.store.select(getClient)),
			filter(([, client]) => !!client),
			concatMap(([, client]) => {
				return this.venueGateway
					.filter({
						favOnly: true,
						clientId: client!.id,
						venuesAlreadyInView: [],
						categoryId: client?.selectedCategory?.categoryId,
					})
					.pipe(
						map(venues =>
							VenueActions.filterVenuesByFavoritesSuccess({ favoritesVenues: venues })
						),
						catchError(error => of(VenueActions.filterVenuesByFavoritesFailure({ error })))
					)
			})
		)
	})
}
